/**
 * BalanceDisplay — shows token balance, native balance, insufficient warning, and Max button.
 *
 * Story 5.2 Task 5
 */

import { parseUnits } from "viem";

import { useTokenBalance } from "../../hooks/chain/use-token-balance";
import { formatNativeBalance } from "../../lib/format";
import { getNativeCurrencySymbol } from "../../lib/wagmi-config";

type BalanceDisplayProps = {
	tokenAddress: string;
	tokenSymbol: string;
	tokenDecimals: number;
	amountIn: string;
	onMaxClick: (maxAmount: string) => void;
};

export function BalanceDisplay({
	tokenAddress,
	tokenSymbol,
	tokenDecimals,
	amountIn,
	onMaxClick,
}: BalanceDisplayProps) {
	const { balance, nativeBalance, formatted, isLoading } = useTokenBalance(
		tokenAddress,
		tokenDecimals
	);

	const nativeSymbol = getNativeCurrencySymbol();

	// Check insufficient balance
	let isInsufficient = false;
	if (balance !== undefined && amountIn) {
		try {
			const amountWei = parseUnits(amountIn, tokenDecimals);
			isInsufficient = amountWei > balance;
		} catch {
			// Invalid input — don't show warning
		}
	}

	if (balance === undefined && !isLoading) {
		// No balance data (wallet disconnected)
		return null;
	}

	return (
		<div className="flex items-center justify-between text-xs">
			<div className="flex items-center gap-1.5 text-pastel-gray">
				{isLoading ? (
					<span>Loading...</span>
				) : (
					<>
						<span>
							Balance: {formatted} {tokenSymbol}
						</span>
						{nativeBalance !== undefined && (
							<span className="text-pastel-gray/60">
								({formatNativeBalance(nativeBalance)} {nativeSymbol})
							</span>
						)}
					</>
				)}
			</div>
			<div className="flex items-center gap-2">
				{isInsufficient && <span className="text-red-500 font-medium">Insufficient balance</span>}
				{balance !== undefined && balance > BigInt(0) && (
					<button
						type="button"
						onClick={() => onMaxClick(formatted)}
						aria-label="Use maximum balance"
						className="text-pastel-vibrant-blue font-semibold hover:underline"
					>
						Max
					</button>
				)}
			</div>
		</div>
	);
}
